<template>
  <div>
    <div v-if="loading" class="w-full h-full flex justify-center items-center loading">
      <p class="text-gray-400 font-bold animate-pulse">Searching...</p>
    </div>

    <PageHeader :title="isSmallDevice?'':'サブタスク一覧'" class="list">
      <template #title-header-content>
        <div class="flex-1 pl-2 flex ml-6 justify-end items-center">

          <div class="flex flex-wrap">
            <span class="text-gray-100 font-bold text-xs">メイン</span>
            <Toggle v-model="toggle.mainTask" size="small" class="pl-2"/>
          </div>

          <div class="ml-4 flex flex-wrap">
            <span class="text-gray-100 font-bold text-xs">受注</span>
            <Toggle v-model="toggle.orderTask" size="small" class="pl-2"/>
          </div>

          <div class="ml-4 flex flex-wrap">
            <span class="text-gray-100 font-bold text-xs">DC雇入</span>
            <Toggle v-model="toggle.employmentTask" size="small" class="pl-2"/>
          </div>

          <div v-if="isRegularStaffAuthorized" class="ml-4 flex flex-wrap" v-tooltip="'管理者・人事部以外は同じ部署のみ表示します'">
            <span class="text-gray-100 font-bold text-xs">社員雇入</span>
            <Toggle v-model="toggle.employmentRegularTask" size="small" class="pl-2"/>
          </div>

          <!-- メイン絞り込み -->
          <div class="rounded p-1 ml-5 bg-opacity-20 bg-light-blue-600 border border-light-blue-300">
            <div class="text-xs font-bold text-center text-light-blue-50">メイン</div>
            <div class="flex">

              <!-- メイン担当者絞込み -->
              <PrimaryButton
                v-tooltip="'メイン担当者が自分'"
                :buttonContents="'要'"
                :class="btn.mainUser ? 'on' : 'off'"
                size="xs"
                :text="'担当分'"
                class="mx-1 block whitespace-nowrap"
                @click="btn.mainUser=!btn.mainUser">
              </PrimaryButton>

              <!-- 未完了（メイン） -->
              <PrimaryButton
                v-tooltip="'未完了（メイン）'"
                :buttonContents="'要'"
                :class="btn.incompleteMain ? 'on' : 'off'"
                size="xs"
                :text="isSmallDevice ? '未完了' : ''"
                class="mx-1 block"
                @click="btn.incompleteMain=!btn.incompleteMain">
                <Icon v-if="!isSmallDevice" slot="before" iconName="ExclamationCircle" iconType="solid" :strokeWidth="2" class="h-5 w-5" />
              </PrimaryButton>
            </div>
          </div>

          <!-- サブタスク絞り込み -->
          <div class="rounded p-1 ml-3 bg-opacity-20 bg-light-blue-600 border border-light-blue-300">
            <div class="text-xs font-bold text-center text-light-blue-50">サブ</div>
            <div class="flex">

              <!-- サブタスク担当者絞込み -->
              <PrimaryButton
                v-tooltip="'サブ担当者が自分と未定分'"
                :buttonContents="'要'"
                :class="btn.subUser ? 'on' : 'off'"
                size="xs"
                :text="'担当分・未定'"
                class="mx-1 block whitespace-nowrap"
                @click="btn.subUser=!btn.subUser">
              </PrimaryButton>

              <!-- 本日までの期限（サブ） -->
              <PrimaryButton
                v-tooltip="'本日までの期限（サブタスク）'"
                :buttonContents="'要'"
                :class="btn.untilToday ? 'on' : 'off'"
                size="xs"
                :text="isSmallDevice ? '本日まで' : ''"
                class="mx-1 block whitespace-nowrap"
                @click="btn.untilToday=!btn.untilToday">
                <Icon v-if="!isSmallDevice" slot="before" iconName="Clock" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
              </PrimaryButton>

              <!-- 未完了（サブタスク） -->
              <PrimaryButton
                v-tooltip="'未完了（サブタスク）'"
                :buttonContents="'要'"
                :class="btn.incompleteSub ? 'on' : 'off'"
                size="xs"
                :text="isSmallDevice ? '未完了' : ''"
                class="mx-1 block whitespace-nowrap"
                @click="btn.incompleteSub=!btn.incompleteSub">
                <Icon v-if="!isSmallDevice" slot="before" iconName="ExclamationCircle" iconType="solid" :strokeWidth="2" class="h-5 w-5" />
              </PrimaryButton>
            </div>
          </div>

          <PrimaryButton text="検索" @click="search()" class="min-w-100px block ml-4" :buttonContents="'要'" />

          <PrimaryButton text="受注取消" :size="isSmallDevice?'xs':'normal'" class="block ml-4 whitespace-nowrap px-2" :class="btn.removeOrder ? 'clickButtonColor' : 'normalButtonColor'" v-tooltip="'受注取消したデータを表示します'" @click="clickRemoveOrder()" :buttonContents="'要'" />

          <PrimaryButton text="クリア" v-if="!isSmallDevice" class="off px-1 block ml-4 text-xs whitespace-nowrap" @click="clear" :buttonContents="'要'" />

          <PrimaryButton text="カレンダー" class="ml-4 bg-blue-100 text-light-blue-700 border-light-blue-700 whitespace-nowrap px-2 text-xs" @click="$router.push({ name: 'TaskCalendar' })">
            <Icon slot="before" iconName="Calendar" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
          </PrimaryButton>

          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{'animate-spin': loading}"
            class="text-white hover:text-gray-300 active:text-gray-400 ml-4 cursor-pointer"
            @click.native="getData()"
          />
        </div>
      </template>
      <template #page-header-content>
        <div class="flex justify-end">

          <div class="text-light-blue-100 font-bold text-sm mr-auto mt-2 cursor-pointer whitespace-nowrap" @click="clickKeword()">
            <span v-if="keywordOpen" class="text-base">▼</span>
            <span v-else class="text-base">▶</span>
            <span class="pl-2">キーワード検索</span>
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <WmsSuggestInput
              name="category"
              :dynamic="false"
              :incrementalSearch="false"
              :selectionItems="categoryList"
              displayField="text"
              placeholder="お問合わせカテゴリー"
              v-model="text.category"
              :clearable="true"
              class="sug p-0.5 border-transparent rounded-md leading-5 focus:outline-none focus:ring-white focus:border-white focus:placeholder-gray-600 focus:text-gray-900 sm:text-sm"
              :class="text.category?'yellow':''"
              :embed="true"
              id="category"
            />
          </div>

          <div id="request-no" class="focus-within:text-gray-400 search-contents">
            <input
              type="tel"
              v-model="text.requestNo"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.requestNo?'bg-yellow-200 font-bold':''"
              placeholder="お問い合わせNo (メイン)"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.titleMain"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.titleMain?'bg-yellow-200 font-bold':''"
              placeholder="タイトル（メイン）"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.toStaffName"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.toStaffName?'bg-yellow-200 font-bold':''"
              placeholder="担当者（メイン or サブ）"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <input
              type="search"
              v-model="text.titleSub"
              class="block w-full py-2 pr-3 border border-transparent rounded-md leading-5 focus:outline-none focus:ring-white focus:border-white focus:placeholder-gray-500 focus:text-gray-900 sm:text-sm"
              :class="text.titleSub?'bg-yellow-200 font-bold':''"
              placeholder="サブタスク名"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <WmsSuggestInput
              name="toDepartment"
              :dynamic="false"
              :incrementalSearch="false"
              :filter="{}"
              :selectionItems="toDepartmentList"
              displayField="name"
              placeholder="部署（サブ）"
              v-model="text.toDepartment"
              :clearable="true"
              class="sug p-0.5 border-transparent rounded-md leading-5 focus:outline-none focus:ring-white focus:border-white focus:placeholder-gray-600 focus:text-gray-900 sm:text-sm"
              :class="text.toDepartment?'yellow':''"
              :embed="true"
              id="toDepartment"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <DateSelect
              class="filter-date"
              ref="dateSelect1"
              :presetName="text.createPeriodName"
              :period="text.createPeriod"
              :none="true"
              :titleText="text.createPeriod.start?'':'作成日（メイン）'"
              :class="text.createPeriod&&text.createPeriod.start?'yellow':''"
              width="200px"
              @change="periodChange1"
            />
          </div>

          <div class="focus-within:text-gray-400 search-contents">
            <DateSelect
              class="filter-date"
              ref="dateSelect2"
              :presetName="text.limitPeriodName"
              :period="text.limitPeriod"
              :none="true"
              :titleText="text.limitPeriod&&text.limitPeriod.start?'':'期限日（メイン）'"
              :class="text.limitPeriod&&text.limitPeriod.start?'yellow':''"
              width="200px"
              @change="periodChange2"
            />
          </div>
        </div>
        <div v-if="keywordOpen||text.searchKeyword" class="mt-2">
          <input
            type="search"
            v-model="text.searchKeyword"
            class="w-full bg-light-blue-600 bg-opacity-20 py-2 pl-10 pr-3 border border-light-blue-300 rounded-md leading-5 keyword placeholder-light-blue-200 sm:text-sm text-light-blue-100  font-bold"
            placeholder="キーワード検索（フリガナ（面接・雇入）、物件名（面接・雇入） から部分一致で検索）スペースでAND検索します。"
          />
        </div>
      </template>
    </PageHeader>

    <main class="absolute w-screen bottom-4 -mt-2" :class="keywordOpen||text.searchKeyword?'top-43':'top-32'">
      <div class="mx-auto px-2 sm:px-4 lg:px-8 h-full">
        <div class="bg-white rounded-md border border-gray-200 ---overflow-auto h-full table-width">

          <div v-if="searchResults && searchResults.length" class="h-full overflow-y-auto" ref="subList" @scroll="onScroll()">

            <table class="w-full overflow-auto">
              <thead class="sticky top-0 t-thead">
                <tr v-if="searchResults && searchResults.length" class="">
                  <th v-for="(col, idx) in columns" :key="idx" scope="col" class="px-2 py-3 text-xs text-gray-600 tracking-wider whitespace-nowrap cursor-pointer" :class="col.class" @click="sortSwitching(col)" v-tooltip="sortMsg(col)">
                    {{ col.title }}
                    <span v-if="text.sortItem==col.item">
                      <Icon iconName="ArrowDown" :class="text.sortBy==1?'text-blue-700':'text-gray-400'" class="w-3 h-4 mr-0.5 inline"/>
                      <Icon iconName="ArrowUp" :class="text.sortBy==-1?'text-blue-700':'text-gray-400'" class="w-3 h-4 mr-0.5 inline"/>
                    </span>
                    <span v-else-if="col.title">
                      <Icon iconName="ArrowDown" class="w-3 h-4 mr-0.5 inline text-gray-400"/>
                      <Icon iconName="ArrowUp" class="w-3 h-4 mr-0.5 inline text-gray-400"/>
                    </span>
                  </th>
                </tr>
              </thead>

              <tr v-for="(row, idx) in searchResults" :key="idx" class="tracking-wider text-sm hover-color text-gray-700 border-b border-gray-300">
                <!-- 期限 -->
                <td class="row whitespace-nowrap">
                  <div class="w-24">{{ row.limitDate }}</div>
                </td>
                <!-- タイトル -->
                <td class="row text-xs">
                  <div class="text-xs text-green-600">No.{{ row.requestNo }}</div>
                  <div class="truncate w-72">{{ row.inquiry_title }}</div>
                </td>
                <!-- 担当者 -->
                <td class="row" >
                  <div class="truncate w-24">{{ row.toStaffName }}</div>
                </td>
                <!-- お問い合わせカテゴリー -->
                <td class="row text-xs">
                  <div class="truncate w-36">{{ row.categoryString }}</div>
                </td>
                <!-- 詳細 -->
                <td class="row whitespace-nowrap cursor-pointer" @click="openSubTask(row)">
                  <div class="text-blue-600 hover:text-blue-900">詳細</div>
                </td>
                <!-- 進捗 -->
                <td class="row ">
                  <TaskStatusTracker :record="row" style="width: fit-content; min-width: 300px;" @toParent="notification" @changeLoading="changeLoading"></TaskStatusTracker>
                </td>
              </tr>
            </table>
          </div>

          <div class="text-transparent text-xs">{{ reload }}</div>

          <div v-if="!loading && searchResults.length === 0" class="w-full h-full flex justify-center items-center text-gray-400">
            <EmptyMessage
              iconName="Document"
              message="該当する案件はありません。"
            />
          </div>
        </div>
      </div>
    </main>
    <DataSelectorModal
      v-model="dataSelectorShow"
      :title="dataSelectorParams.title"
      :subtitle="dataSelectorParams.subtitle"
      :options="dataSelectorParams.options"
      :selectCallback="dataSelectorParams.selectCallback"
    />
    <!-- 送付状モーダル -->
    <CoverLetterModal
      v-model="coverLetterShow"
      :open="coverLetterShow"
      :target="dataSelectorParams"
      @close="coverLetterShow = false"
    />
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import DateSelect from '@components/DateSelect.vue'
import EmptyMessage from '@components/EmptyMessage.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import Toggle from '@components/Toggle.vue'
import DataSelectorModal from '@components/DataSelectorModal.vue'
import TaskStatusTracker from '../components/TaskStatusTracker.vue'
import CoverLetterModal from '../../Estimation/components/CoverLetterModal.vue'
import { categorySelections } from '@libs/constants'
import * as requestPostManager from '@managers/requestPostManager'
import * as estimateManager from '@managers/estimateManager'
import * as utils from '@libs/utils'
import * as dialogs from '@libs/dialogs'
import _ from 'lodash'

import PageModel from '../../main/pages/PageModel.vue'

// ボタン検索セッションストレージ（メニューへ行ってもリセットされない）
const stBtn = 'subListBtn'
// トグル検索セッションストレージ（メニューへ行ってもリセットされない）
const stToggle = 'subListToggle'
// 文字列検索セッションストレージ（メニューへ行くとリセット）
const stText = 'subListText'
// 取得した最後の作成日、問い合わせNo（カーソルが下にきた場合に、このキーの次から取得。しおりとなる）
const stLast = 'subListLast'

// 検索対象プロパティ（メイン）
const searchKeysMain = [
  'interviewsKana', // 面接カナ
  'employmentKana', // 雇入カナ
  'interviewsAppliedWorkLocationAddress', // 面接物件名
  'employmentBuildingName' // 雇入物件名
]

export default {
  extends: PageModel,
  components: {
    Icon,
    DateSelect,
    PageHeader,
    EmptyMessage,
    PrimaryButton,
    WmsSuggestInput,
    Toggle,
    DataSelectorModal,
    TaskStatusTracker,
    CoverLetterModal
  },

  data() {
    return {
      // 一覧データ
      searchResults: [],
      loading: false,
      firstRefresh: true,
      reload: 0,
      // キーワード検索open
      keywordOpen: false,
      // 取得したデータの最後の行のソート項目
      last: {
        scrollEnd: false
      },
      // 社員雇い入れ切り替えタブ表示
      isRegularStaffAuthorized: false,

      // カラム
      columns: [
        { item: 'limitDate', title: 'メイン期限', class: 'text-left th-limit_date' },
        { item: 'inquiry_title', title: 'タイトル', class: 'text-left th-inquiry_title' },
        { item: 'toStaffName', title: '担当者', class: 'text-left th-toStaff_name' },
        { item: 'category', title: 'お問い合わせカテゴリー', class: 'text-left th-category_string' },
        { item: '', fixedName: '詳細', title: '', class: 'text-left th-view_link' },
        { item: 'earliestLimitDate', title: '進捗', type: 'taskStatusTracker th-task_progress' }
      ],

      // トグル検索条件（メニュー画面に戻ってもリセットされない）
      toggle: {
        // メインタスク
        mainTask: true,
        // 受注タスク
        orderTask: false,
        // DC雇い入れ
        employmentTask: false,
        // 社員雇い入れ
        employmentRegularTask: false,
      },
      // ボタン検索条件（メニュー画面に戻ってもリセットされない）
      btn: {
        // 自分の担当（メイン）
        mainUser: true,
        // 自分の担当（サブ）
        subUser: true,
        // 本日までの期限
        untilToday: false,
        // 未完了検索（メイン）
        incompleteMain: true,
        // 未完了検索（サブ）
        incompleteSub: true,
        // 取り消しした受注タスク
        removeOrder: false
      },
      // 検索欄記入（メニュー画面に戻るとリセット）
      text: {
        // フリー検索キーワード
        searchKeyword: '',
        // 作成（メイン）
        createPeriodName: 'custom',
        createPeriod: {},
        // 期限（メイン）
        limitPeriodName: 'custom',
        limitPeriod: {},
        // お問い合わせNo
        requestNo: '',
        // タイトル（メイン）
        titleMain: '',
        // タイトル（サブ）
        titleSub: '',
        // 担当者（メイン・サブ）
        toStaffName: '',
        // お問い合わせカテゴリー
        category: '',
        // 部署（サブ）
        toDepartment: '',
        // ソート対象項目
        sortItem : 'createDate',
        // 昇順降順（初期値：降順）
        sortBy: -1
      },
      // お問い合わせカテゴリーリスト
      categoryList: categorySelections,
      // 見積り遷移時の見積りを選択するモーダル
      dataSelectorShow: false,
      dataSelectorParams: {
        title: '',
        subtitle: '',
        options: {
          formId: '',
          displayFields: [],
          sortField: '',
          selectable: true,
          filter: {}
        }
      },
      // 送付状モーダル
      coverLetterShow: false,
    }
  },

  computed: {
    /**
     * 取得した一覧データ
     */
    list() {
      // 過去に取得したデータをそのまま取得（stateでOKなぜなら取得し終わったらこの画面で空に変更するから）
      return this.$store.state.subList
    },

    /**
     * ログインユーザー名そのまま
     */
    userNameWithKana() {
      return this.$store.getters.user.user_name
    },

    /**
     * ログインユーザー名カナ削除
     */
    userName() {
      return utils.deleteKana(this.$store.getters.user.user_name)
    },

    /**
     * ログインユーザーID
     */
    userId() {
      return this.$store.getters.user.id
    },

    /**
     * メインの所属部署名
     */
    mainDepartment() {
      const res = this.$store.getters.loginUserData
      return res.departmentName
    },

    /**
     * 所属部署（兼任含む）
     */
    dualPosition() {
      return this.$store.getters.dualPosition
    },

    /**
     * 所属部署名のみの配列
     */
    dualPositionNames() {
      if (this.dualPosition && this.dualPosition.length) {
        const res = this.dualPosition.map(row => row.departmentName)
        if (res && res.length) {
          return res
        }
      }
      return []
    },

    /**
     * 所属部署IDのみの配列
     */
    dualPositionIds() {
      if (this.dualPosition && this.dualPosition.length) {
        const res = this.dualPosition.map(row => row.departmentId)
        if (res && res.length) {
          return res
        }
      }
      return []
    },

    /**
     * 権限
     */
    auth() {
      return this.$store.getters.auth
    },

    /**
     * 管理者権限あり
     */
    admin() {
      return utils.admin(this.auth)
    },

    /**
     * 社員面接権限あり
     */
    regularStaffAuth() {
      if (this.auth.regularStaffAuth) {
        return true
      }
      return false
    },

    /**
     * 人事総務経理所属か否か
     */
    manager() {
      return utils.manager(this.dualPosition)
    },

    /**
     * スマホ or タブレット
     */
    isSmallDevice() {
      return (utils.deviceInfo.isMobile || utils.deviceInfo.isTablet)
    },

    /**
     * 部署検索リスト
     */
    toDepartmentList() {
      const departmentList = this.$store.getters.departmentList
      const transDepartmentList = this.$store.getters.transDepartmentList
      return [ ...departmentList, ...transDepartmentList ]
    },

    /**
     * G4以上か
     */
    isGroup4() {
      return utils.isGroup4(this.$store.getters.user.group_num)
    }
  },

  created() {
    this.getRegularStaffAuthorized()
  },

  mounted() {
    this.$nextTick(() => {
      this.loadCondition()
      this.getData()
    })
  },

  methods: {
    /**
     * データ取得
     */
    async getData(next) {
      // 詳細画面から戻った初回の場合
      const lastKeyItem = sessionStorage.getItem(stLast)
      let lastKey
      if (lastKeyItem) {
        lastKey = JSON.parse(lastKeyItem)
      }

      // サブタスク閲覧から戻ってきた場合
      if (this.firstRefresh && lastKey && this.$route.query.sub && this.$route.query.sub == true && this.list && this.list.length) {
        // 先ほどのデータを表示
        this.searchResults = utils.clone(this.list)
        this.last = lastKey

        // カーソル
        await utils.wait(200)
        const scrollY = this.$store.state.subListScroll
        if (scrollY) {
          // 画面復帰時にスクロール位置を戻す
          this.$nextTick(() => {
            this.$refs.subList.scrollTo({
              top: scrollY,
              behavior: 'smooth'
            })
          })
        }

        this.$store.dispatch('setSubList', [])
        this.$store.dispatch('setSubListScroll', null)
      } else {
        // 検索結果がゼロになる場合
        if (!this.toggle.mainTask && !this.toggle.orderTask && !this.toggle.employmentTask && !this.toggle.employmentRegularTask) {
          await dialogs.showErrorDialog('トグルの表示が全てOFF', '「メインタスク」などの表示が全てOFFになっています。どれかONにしてください。')
          return
        }
        // 担当者が複数
        if (this.btn.mainUser && this.btn.subUser && this.text.toStaffName) {
          await dialogs.showErrorDialog('担当分がON、かつ担当者検索あり', `担当者検索が自分の担当分、かつ「${this.text.toStaffName}」も検索になっています。どちらか一方にしてください。`)
          return
        }
        await this.fetchSubList(next)
      }
      this.firstRefresh = false
    },

    /**
     * 下までスクロールした場合、次の50件を取得
     */
    onScroll:  _.throttle(async function() {
      // データ取得中
      if (this.loading) {
        return
      }
      const el = this.$refs.subList
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {

        // データがない場合
        if (this.last.scrollEnd) {
          console.log('次のデータなし')
          return
        }
        // しおりがない場合
        if (!this.last.requestNo) {
          console.log('しおりなし')
          return
        }
        this.loading = true
        console.log('次の100件を取得')
        console.log(this.last)
        await this.getData(this.last)
      }
    }, 200),

    /**
     * 検索
     */
    async fetchSubList(next) {
      if (!next) {
        this.searchResults = []
      }

      this.loading = true
      const resList = await requestPostManager.searchSubList(
        this,
        searchKeysMain,
        this.btn,
        this.toggle,
        this.text,
        this.userName,
        this.userNameWithKana,
        this.userId,
        this.isGroup4,
        this.admin,
        this.regularStaffAuth,
        this.manager,
        this.mainDepartment,
        this.dualPositionNames,
        this.dualPositionIds,
        next
      )
      const list = resList.list
      console.log(list.length)

      this.searchResults = this.searchResults.concat(list)
      console.log('総件数：' + this.searchResults.length)

      this.loading = false

      // ページングのために最後の行の項目を保存
      if (list && list.length) {
        const res = resList.lastData
        this.last = {
          scrollEnd: false,
          [this.text.sortItem]: res[this.text.sortItem],
          requestNo: res.requestNo
        }
        this.last.createDate = res.createDate
        console.log('最終行キー項目:::')
        console.log(this.last)
      } else {
        this.last.scrollEnd = true
      }
      this.setSessionStorage(stLast, this.last)
    },

    /**
     * カラムクリックでソート変更
     */
    sortSwitching(tar) {
      if (this.loading) {
        return
      }
      if (!tar.item) {
        return
      }
      // 同じカラムを再度クリック
      if (tar.item == this.text.sortItem) {
        if (this.text.sortBy == 1) {
          this.text.sortBy = -1
        } else {
          // 初期値に戻す
          this.text.sortItem = 'createDate'
        }
      } else {
        this.text.sortItem = tar.item
        this.text.sortBy = 1
      }
      this.getData()
    },

    /**
     * ソートの説明
     * @param tar 対象のカラム
     */
    sortMsg(tar) {
      if (this.loading || !tar.item) {
        return ''
      }
      let title = tar.title
      if (tar.item == 'earliestLimitDate') {
        title = '未完了のサブタスクの期限'
      }
      // 同じカラムを再度
      if (tar.item == this.text.sortItem) {
        if (this.text.sortBy == 1) {
          return 'クリックすると「' + title + '」を降順で検索します'
        } else {
          // 初期値に戻す
          return 'クリックすると' + '初期値（メイン作成日の降順）のソートに戻して検索します'
        }
      } else {
        return 'クリックすると「' + title + '」を昇順で検索します'
      }
    },

    /**
     * セッションストレージから検索条件を取得
     */
    loadCondition() {
      // 見積参照から遷移した場合
      if (this.$route.query.requestNo) {
        this.fromEstimateView()
      } else {
        const btn = sessionStorage.getItem(stBtn)
        const toggle = sessionStorage.getItem(stToggle)
        const text = sessionStorage.getItem(stText)

        if (btn) {
          this.btn = JSON.parse(btn)
        }

        if (toggle) {
          this.toggle = JSON.parse(toggle)
        }

        if (text) {
          this.text = JSON.parse(text)
          if (this.text.searchKeyword) {
            this.keywordOpen = true
          }
        }
        // カレンダーに反映
        if (this.text.createPeriod && this.text.createPeriod.start) {
          this.$refs.dateSelect1.setRange(this.text.createPeriod, this.text.createPeriodName, true)
        }
        // カレンダーに反映
        if (this.text.limitPeriod && this.text.limitPeriod.start) {
          this.$refs.dateSelect2.setRange(this.text.limitPeriod, this.text.limitPeriodName, true)
        }
        this.reload++
      }
    },

    /**
     * 受注取消表示ボタン
     */
    clickRemoveOrder() {
      // ONにする場合
      if (!this.btn.removeOrder) {
        // 未完了（メイン）を解除
        this.btn.incompleteMain = false
        // 未完了（サブ）を解除
        this.btn.incompleteSub = false

        // 表示・非表示トグル
        this.toggle =  {
          // メインタスク
          mainTask: false,
          // 受注タスク
          orderTask: true,
          // DC雇い入れ
          employmentTask: false,
          // 社員雇い入れ
          employmentRegularTask: false,
        }
      // OFFにする場合
      } else {
        // 未完了（メイン）をON
        this.btn.incompleteMain = true
        // 未完了（サブ）をON
        this.btn.incompleteSub = true
      }
      this.btn.removeOrder = !this.btn.removeOrder
      this.reload++
    },

    /**
     * ソート change イベント
     */
    async changeSort(data) {
      this.text.sortItem = data.item
      this.text.sortBy = data.by
      // セッションストレージに保存
      this.setSessionStorage(stText, this.text)
      this.getData()
    },

    /**
     * キーワード検索クリック
     */
    clickKeword() {
      if (this.text.searchKeyword) {
        this.keywordOpen = true
      } else {
        this.keywordOpen = !this.keywordOpen
      }
    },

    /**
     * 作成（メイン）検索 変更
     */
    periodChange1(range, presetName) {
      this.text.createPeriod = range
      this.text.createPeriodName = presetName
    },

    /**
     * 期限（メイン）検索 変更
     */
    periodChange2(range, presetName) {
      this.text.limitPeriod = range
      this.text.limitPeriodName = presetName
    },

    /**
     * 見積りから遷移した場合の検索
     */
    fromEstimateView() {
      // 見積参照から遷移した場合
      if (this.$route.query.requestNo) {
        // 検索履歴をクリア
        this.removeSettion()

        // 問い合わせNoで検索をかける
        this.text.requestNo = this.$route.query.requestNo

        // メイン担当者絞り込み解除
        this.btn.mainUser = false

        // サブ担当者絞り込み解除
        this.btn.subUser = false

        // 当日のみを解除
        this.btn.untilToday = false

        // 未完了（メイン）を解除
        this.btn.incompleteMain = false

        // 未完了（サブ）を解除
        this.btn.incompleteSub = false
      }
    },

    /**
     * 社員雇い入れ切り替えタブを表示
     */
    async getRegularStaffAuthorized() {
      // 管理者権限あり、ログインユーザーの所属部署（兼任も含めて）が人事部、true
      const dualPosition = this.$store.getters.dualPosition
      const isEmployment = utils.isRegularStaffAuthorized(this.$store.getters.auth, dualPosition)
      // グループ4以上なら同じ部署のみOK
      const group4 = utils.isGroup4(this.$store.getters.user.group_num)
      if (isEmployment || group4) {
        this.isRegularStaffAuthorized = true
      } else {
        this.isRegularStaffAuthorized = false
      }
    },

    /**
     * 検索ボタン 押下
     */
    search() {
      this.last.scrollEnd = false
      // セッションストレージに検索条件保存
      this.setSessionStorage(stBtn, this.btn)
      this.setSessionStorage(stToggle, this.toggle)
      this.setSessionStorage(stText, this.text)
      this.getData()
    },

    /**
     * 検索条件をクリア
     */
    clear() {
      this.removeSettion()
      this.$router.go({path: this.$router.currentRoute.path, force: true})
    },

    /**
     * セッションストレージ削除
     */
    removeSettion() {
      sessionStorage.removeItem(stBtn)
      sessionStorage.removeItem(stToggle)
      sessionStorage.removeItem(stText)
      sessionStorage.removeItem(stLast)
    },

    /**
     * 検索条件をセッションストレージへ保存
     */
    setSessionStorage(name, searchCondition) {
      const searchStr = JSON.stringify(searchCondition)
      sessionStorage.setItem(name, searchStr)
    },

    /**
     * サブタスクOPEN
     * @param row 対象行
     */
    openSubTask(row) {
      // メインタスク画面から戻ってきたときのために一覧を保存
      this.$store.dispatch('setSubList', this.searchResults)
      this.$store.dispatch('setSubListScroll', this.$refs.subList.scrollTop)
      // 受注以外
      if (row.type == 'apollo_request_post') {
        this.$router.push({ name: 'TaskRequest', query: { requestPostId: row._id }})
      // 受注
      } else {
        this.$router.push({ name: 'TaskRequest', query: { orderId: row._id, mode: 1 }})
      }
    },

    /**
     * テーブルより通知イベント
     */
    async notification(msg, param) {
      this.dataSelectorParams = {}
      // 見積選択表示
      if (msg == 'showEstimateSelector') {
        this.dataSelectorParams = {
          title: '見積の選択',
          subtitle: '',
          options: {
            formId: 'rp_estimates',
            displayFields: [ 'estimateNo', 'subject', 'salesPersonName' ],
            searchFields: [ 'estimateNo', 'subject', 'salesPersonName' ],
            sortField: 'estimateNo',
            selectable: true,
            filter: {
              taskRequestId: param._id
            }
          },
          selectCallback: (selected) => {
            this.dataSelectorShow = false
            this.$router.push({
              name: 'EstimateForm',
              params: {
                copyEstimateNo: selected.estimateNo
              }
            })
          }
        }
        this.dataSelectorShow = true
      } else if (msg == 'coverLetterShow') {
        this.dataSelectorParams = await estimateManager.getEstimate(this, param.estimateNo)
        this.coverLetterShow = true
      } else if (msg == 'refresh') {
        this.getData()
      }
    },

    /**
     * loadingugnidaolgnd処理（TaskStatusTrackerで使用）
     * @param val 
     */
    changeLoading(val) {
      this.loading = val
    }
  },
}
</script>

<style scoped>
  .list {
    min-width: 1390px;
  }

  .table-width {
    min-width: 1310px;
  }

  .row {
    padding: 10px 8px;
  }

  .t-thead {
    z-index: 20;
    background: #ebf0fb;
  }

  .hover-color:hover {
    background: rgba(225, 244, 247, 0.67) !important;
    transform: translateY(-2px);        /* 上に 2px 浮かせる */
    box-shadow: 0 2px 2px rgba(0,0,0,0.15); /* 影をつける */
    font-weight: bold;
  }

  #category >>> input[type=text] {
    height: 39px;
    margin-top: -2px;
    border-radius: 0.375rem !important;
  }

  #toDepartment >>> input[type=text] {
    height: 39px;
    margin-top: -2px;
    border-radius: 0.375rem !important;
  }

  .filter-date >>> div {
    color: rgb(70, 70, 70) !important;
  }

  .filter-date >>> input[type=text] {
    background: white !important;
    font-size: small !important;
  }

  #request-no >>> input[type=search]::placeholder {
    font-size: small;
  }

  .yellow >>> input[type=text] {
    background-color: #efdf8a !important;
    font-weight: bold !important;
  }
</style>